import { Box, Button, Tooltip, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import { useNavigate } from "react-router-dom";

import { ON_BEHALF_OF_TOKEN } from "utils/localStorage";

import { OutreachAutomation } from "../types";

const TYPE_CONFIG: Record<
  string,
  { label: string; icon: string; iconBg: string; iconColor?: string }
> = {
  bento_database: {
    label: "Bento Database",
    icon: "fa-solid fa-database",
    iconBg: "#e8f4fd",
    iconColor: "#1976d2",
  },
  instagram_tracked_creators: {
    label: "Instagram Tracked Creators",
    icon: "fa-brands fa-instagram",
    iconBg: "#fce8ec",
    iconColor: "#c03d5e",
  },
  google_places: {
    label: "Google Places",
    icon: "fa-solid fa-location-dot",
    iconBg: "#d5f0e5",
    iconColor: "#1a7a52",
  },
};

interface AutomationCardProps {
  automation: OutreachAutomation;
  onPause: (id: number) => void;
  onResume: (id: number) => void;
  onDelete: (id: number) => void;
  onEdit: (automation: OutreachAutomation) => void;
  onRun: (id: number) => void;
  isUpdating: boolean;
}

export default function AutomationCard({
  automation,
  onPause,
  onResume,
  onDelete,
  onEdit,
  onRun,
  isUpdating,
}: AutomationCardProps) {
  const navigate = useNavigate();
  const isAdmin = Boolean(sessionStorage.getItem(ON_BEHALF_OF_TOKEN));
  const typeConfig = TYPE_CONFIG[automation.type] ?? {
    label: automation.type,
    icon: "fa-solid fa-gear",
    iconBg: grey[200],
  };
  const isActive = automation.status === "active";
  const isPending = automation.status === "pending";
  const isDraft = automation.status === "draft";
  const isPaused = automation.status === "paused";
  const configDetails = getConfigDetails(automation);
  const pendingTooltip = isPending
    ? `We're searching for brands for you. ${
        automation.startDate
          ? `Pitching will begin the week of ${formatStartDate(automation.startDate)}.`
          : "Pitching will begin next week."
      }`
    : undefined;

  return (
    <Box
      onClick={() => {
        if (isDraft) {
          const step = automation.configuration ? "template" : "configure";
          navigate(`new/${automation.id}/${step}`);
        } else {
          onEdit(automation);
        }
      }}
      sx={{
        backgroundColor: isDraft ? grey[50] : "white",
        border: isDraft ? `1px dashed ${grey[300]}` : `1px solid ${grey[200]}`,
        borderRadius: "10px",
        pt: "17px",
        px: "17px",
        pb: "17px",
        overflow: "hidden",
        cursor: "pointer",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        {/* Left: Icon + Info */}
        <Box sx={{ display: "flex", gap: 1.5, flex: 1, minWidth: 0 }}>
          {/* Icon — matches SourceSelection style */}
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "10px",
              backgroundColor:
                isDraft || isPaused ? grey[200] : typeConfig.iconBg,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Box
              component="span"
              className={typeConfig.icon}
              sx={{
                fontSize: 18,
                color:
                  isDraft || isPaused
                    ? grey[400]
                    : typeConfig.iconColor ?? "white",
              }}
            />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Title */}
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: 16,
                lineHeight: "24px",
                color: isDraft ? grey[500] : "#014339",
                mb: 0.25,
              }}
            >
              {automation.name || typeConfig.label}
            </Typography>

            {/* Config details */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: { xs: 0.25, sm: 0.5 },
              }}
            >
              {/* Source type subtitle (when name is set) */}
              {automation.name && (
                <Typography
                  sx={{
                    fontSize: 12,
                    color: isDraft ? grey[400] : grey[600],
                    lineHeight: "16px",
                  }}
                >
                  {typeConfig.label}
                </Typography>
              )}

              {/* Config details subtitle */}
              {configDetails.subtitle && (
                <Typography
                  sx={{
                    fontSize: 12,
                    color: isDraft ? grey[400] : grey[600],
                    lineHeight: "16px",
                  }}
                >
                  {configDetails.subtitle}
                </Typography>
              )}

              {/* Draft hint */}
              {isDraft && (
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 500,
                    color: grey[400],
                    lineHeight: "16px",
                  }}
                >
                  Continue setup to start this automation
                </Typography>
              )}

              {/* Stats row */}
              {!isDraft && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    columnGap: 1.5,
                    rowGap: 0,
                    flexWrap: "wrap",
                  }}
                >
                  <StatItem label="Frequency:" value="Weekly" />
                  <StatItem
                    label="Per Week:"
                    value={`~${automation.brandCount}`}
                  />
                  {configDetails.locations && (
                    <StatItem
                      label="Locations:"
                      value={configDetails.locations}
                    />
                  )}
                  {configDetails.categories && (
                    <StatItem
                      label="Categories:"
                      value={configDetails.categories}
                    />
                  )}
                  {configDetails.tracking && (
                    <StatItem
                      label="Tracking:"
                      value={configDetails.tracking}
                    />
                  )}
                </Box>
              )}

              {/* Last run timestamp */}
              {!isDraft && automation.lastRunAt && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                  }}
                >
                  <Box
                    component="span"
                    className="fa-regular fa-clock"
                    sx={{
                      fontSize: 11,
                      color: grey[400],
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 500,
                      color: grey[400],
                      lineHeight: "16px",
                    }}
                  >
                    Last run {formatTimeAgo(automation.lastRunAt)}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        {/* Right: Action buttons */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            gap: 0.75,
            flexShrink: 0,
          }}
        >
          {isDraft ? (
            <>
              {/* Row 1: Badge */}
              <StatusBadge status={automation.status} />
              {/* Row 2: Continue + Delete */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={isUpdating}
                  onClick={(e) => {
                    e.stopPropagation();
                    const step = automation.configuration
                      ? "template"
                      : "configure";
                    navigate(`new/${automation.id}/${step}`);
                  }}
                  sx={{
                    borderColor: grey[300],
                    color: grey[700],
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: 12,
                    borderRadius: "8px",
                    px: 1.5,
                    py: "5px",
                    minWidth: "auto",
                    lineHeight: "16px",
                    "&:hover": {
                      borderColor: grey[500],
                      backgroundColor: grey[50],
                    },
                  }}
                >
                  Continue
                </Button>
                <Box
                  component="span"
                  className="fa-regular fa-trash-can"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!isUpdating) onDelete(automation.id);
                  }}
                  sx={{
                    fontSize: 14,
                    color: grey[400],
                    cursor: isUpdating ? "default" : "pointer",
                    ml: 0.25,
                    "&:hover": {
                      color: isUpdating ? grey[400] : "#ef4444",
                    },
                  }}
                />
              </Box>
            </>
          ) : (
            <>
              {/* Row 1: Badge + Pause/Resume */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <StatusBadge
                  status={automation.status}
                  tooltip={pendingTooltip}
                />
                {isActive || isPending ? (
                  <Button
                    size="small"
                    disabled={isUpdating}
                    onClick={(e) => {
                      e.stopPropagation();
                      onPause(automation.id);
                    }}
                    sx={{
                      backgroundColor: "#fff7ed",
                      border: "1px solid #ffd6a8",
                      color: "#ca3500",
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: 12,
                      borderRadius: "8px",
                      height: 24,
                      px: 1.5,
                      minWidth: "auto",
                      lineHeight: "16px",
                      "&:hover": {
                        backgroundColor: "#fff0de",
                        borderColor: "#ffbf80",
                      },
                    }}
                  >
                    <Box
                      component="span"
                      className="fa-solid fa-pause"
                      sx={{ fontSize: 10, mr: 0.75 }}
                    />
                    Pause
                  </Button>
                ) : (
                  <Button
                    size="small"
                    disabled={isUpdating}
                    onClick={(e) => {
                      e.stopPropagation();
                      onResume(automation.id);
                    }}
                    sx={{
                      backgroundColor: "transparent",
                      border: `1px solid ${grey[300]}`,
                      color: grey[700],
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: 12,
                      borderRadius: "8px",
                      height: 24,
                      px: 1.5,
                      minWidth: "auto",
                      lineHeight: "16px",
                      "&:hover": {
                        backgroundColor: grey[50],
                        borderColor: grey[500],
                      },
                    }}
                  >
                    <Box
                      component="span"
                      className="fa-solid fa-play"
                      sx={{ fontSize: 10, mr: 0.75 }}
                    />
                    Resume
                  </Button>
                )}
              </Box>

              {/* Row 2: Edit (left) + Review (right) */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <Button
                  size="small"
                  disabled={isUpdating}
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(automation);
                  }}
                  sx={{
                    backgroundColor: "#fafaf6",
                    border: "1px solid rgba(1,67,57,0.1)",
                    color: "#014339",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: 12,
                    borderRadius: "8px",
                    px: 1.5,
                    py: "5px",
                    minWidth: "auto",
                    lineHeight: "16px",
                    "&:hover": {
                      backgroundColor: "rgba(1,67,57,0.06)",
                      borderColor: "rgba(1,67,57,0.2)",
                    },
                  }}
                >
                  <Box
                    component="span"
                    className="fa-solid fa-pen"
                    sx={{ fontSize: 10, mr: 0.75 }}
                  />
                  Edit
                </Button>
                <Button
                  size="small"
                  disabled={isUpdating}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`brands-to-review/${automation.id}`);
                  }}
                  sx={{
                    backgroundColor: "#fafaf6",
                    border: "1px solid rgba(1,67,57,0.1)",
                    color: "#014339",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: 12,
                    borderRadius: "8px",
                    px: 1.5,
                    py: "5px",
                    minWidth: "auto",
                    lineHeight: "16px",
                    "&:hover": {
                      backgroundColor: "rgba(1,67,57,0.06)",
                      borderColor: "rgba(1,67,57,0.2)",
                    },
                  }}
                >
                  <Box
                    component="span"
                    className="fa-solid fa-inbox"
                    sx={{ fontSize: 10, mr: 0.75 }}
                  />
                  Review
                </Button>
                {isAdmin && (
                  <Button
                    size="small"
                    disabled={isUpdating}
                    onClick={(e) => {
                      e.stopPropagation();
                      onRun(automation.id);
                    }}
                    sx={{
                      backgroundColor: "#e8f4fd",
                      border: "1px solid #90caf9",
                      color: "#1565c0",
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: 12,
                      borderRadius: "8px",
                      px: 1.5,
                      py: "5px",
                      minWidth: "auto",
                      lineHeight: "16px",
                      "&:hover": {
                        backgroundColor: "#d0e8f7",
                        borderColor: "#64b5f6",
                      },
                    }}
                  >
                    <Box
                      component="span"
                      className="fa-solid fa-rocket"
                      sx={{ fontSize: 10, mr: 0.75 }}
                    />
                    Run
                  </Button>
                )}
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
}

/* ── Sub-components ────────────────────────────────────────────────── */

function StatusBadge({
  status,
  tooltip,
}: {
  status: string;
  tooltip?: string;
}) {
  const isDraft = status === "draft";
  const isActive = status === "active";
  const isPending = status === "pending";

  const config = isDraft
    ? {
        bg: grey[100],
        border: grey[300],
        color: grey[500],
        icon: "fa-solid fa-pen",
        label: "Draft",
      }
    : isPending
      ? {
          bg: "#fef9c3",
          border: "#fde047",
          color: "#a16207",
          icon: "fa-solid fa-clock",
          label: "Pending",
        }
      : isActive
        ? {
            bg: "#dcfce7",
            border: "#7bf1a8",
            color: "#008236",
            icon: "fa-solid fa-check-circle",
            label: "Active",
          }
        : {
            bg: grey[100],
            border: grey[300],
            color: grey[500],
            icon: "fa-solid fa-pause",
            label: "Paused",
          };

  const badge = (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        px: 1,
        height: 24,
        borderRadius: "4px",
        backgroundColor: config.bg,
        border: `1px solid ${config.border}`,
      }}
    >
      <Box
        component="span"
        className={config.icon}
        sx={{ fontSize: 10, color: config.color }}
      />
      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 600,
          color: config.color,
          lineHeight: "15px",
        }}
      >
        {config.label}
      </Typography>
    </Box>
  );

  if (tooltip) {
    return (
      <Tooltip title={tooltip} arrow placement="top">
        {badge}
      </Tooltip>
    );
  }

  return badge;
}

function StatItem({ label, value }: { label: string; value: string }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 500,
          color: grey[500],
          lineHeight: "16px",
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 600,
          color: grey[800],
          lineHeight: "16px",
          textTransform: "capitalize",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

/* ── Helpers ────────────────────────────────────────────────────────── */

interface ConfigDetails {
  subtitle: string | null;
  locations: string | null;
  categories: string | null;
  tracking: string | null;
}

function getConfigDetails(automation: OutreachAutomation): ConfigDetails {
  const result: ConfigDetails = {
    subtitle: null,
    locations: null,
    categories: null,
    tracking: null,
  };
  const config = automation.configuration;
  if (!config) return result;

  if (automation.type === "google_places") {
    const queries = config.search_queries;
    if (queries?.length) result.subtitle = queries.join(", ");
    else if (config.label) result.subtitle = config.label;

    if (config.geopoints?.length) {
      const locs = config.geopoints.map((g: string) => {
        try {
          const parsed = typeof g === "string" ? JSON.parse(g) : g;
          return parsed.label || parsed.name || "Location";
        } catch {
          return "Location";
        }
      });
      result.locations =
        locs.length <= 2
          ? locs.join(", ")
          : `${locs[0]} +${locs.length - 1} more`;
    }
  }

  if (automation.type === "instagram_tracked_creators") {
    const usernames: string[] = config.instagram_usernames || [];
    if (usernames.length) {
      result.subtitle = usernames.map((u) => `@${u}`).join(", ");
      result.tracking = `${usernames.length} creator${
        usernames.length > 1 ? "s" : ""
      }`;
    }
    if (config.countries?.length) {
      result.locations = `${config.countries.length} ${
        config.countries.length === 1 ? "country" : "countries"
      }`;
    }
  }

  if (automation.type === "bento_database") {
    const params = config.search_parameters;
    if (params?.query) result.subtitle = params.query;

    if (params?.category_ids?.length) {
      result.categories = `${params.category_ids.length} selected`;
    }
    if (params?.geopoints?.length) {
      const locs = params.geopoints.map((g: string) => {
        try {
          const parsed = typeof g === "string" ? JSON.parse(g) : g;
          return parsed.label || parsed.name || "Location";
        } catch {
          return "Location";
        }
      });
      result.locations =
        locs.length <= 2
          ? locs.join(", ")
          : `${locs[0]} +${locs.length - 1} more`;
    }
  }

  return result;
}

function formatStartDate(dateStr: string): string {
  const date = new Date(dateStr + "T00:00:00");
  return date.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
}

function formatTimeAgo(timestamp: number): string {
  const now = Date.now() / 1000;
  const diffSeconds = Math.floor(now - timestamp);
  if (diffSeconds < 60) return "just now";
  const diffMinutes = Math.floor(diffSeconds / 60);
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays === 1) return "1 day ago";
  return `${diffDays} days ago`;
}
